/**
 * LadiesHubCuratedRow - Horizontal Curated Product Row
 * Features: Skeleton loading, inertia scroll, product cards with verified badges
 */

import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Heart, MapPin, Package, CheckCircle, TrendingDown } from 'lucide-react';
import { 
  LadiesHubColors, 
  LadiesHubRadii, 
  LadiesHubSpacing, 
  LadiesHubMotion,
  LadiesHubShadows 
} from '../hubs/ladies/LadiesHubPremiumConfig';
import { formatMoney } from '../utils/formatters';
import { getAdTitle, getAdLocation } from '../utils/adI18n';
import { guardImage } from '../lib/imageTrust';
import WasslaImage from './common/WasslaImage';

const colors = LadiesHubColors;
const radii = LadiesHubRadii;
const spacing = LadiesHubSpacing;
const motionConfig = LadiesHubMotion;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Skeleton shimmer card
function SkeletonCard() {
  return (
    <div
      style={{
        minWidth: 160,
        maxWidth: 160,
        borderRadius: radii.card,
        background: colors.surface,
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      {/* Image skeleton */}
      <div
        style={{
          width: '100%',
          aspectRatio: '1',
          background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
        }}
      />
      {/* Text skeleton */}
      <div style={{ padding: 10 }}>
        <div
          style={{
            height: 12,
            width: '80%',
            borderRadius: 4,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            marginBottom: 8,
          }}
        />
        <div
          style={{
            height: 14,
            width: '50%',
            borderRadius: 4,
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

// Product Card Component
function CuratedProductCard({ product, onNavigate, onToggleFavorite, isFavorite, lang, isRTL }) {
  const title = getAdTitle(product, lang).text || product.title;
  const location = getAdLocation(product, lang);
  const isVerified = product.seller?.verification?.status === 'verified_boutique';

  const categoryKey = product.ladies_category || product.category_id || product.category;
  const imageUrl = guardImage(product.images?.[0], categoryKey);
  const isSvgPlaceholder = imageUrl && imageUrl.startsWith('/assets/placeholders/');

  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={() => onNavigate(`/ad/${product.ad_id}`)}
      style={{
        minWidth: 160,
        maxWidth: 160,
        borderRadius: radii.card,
        background: colors.surface,
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
        cursor: 'pointer',
        flexShrink: 0,
        boxShadow: LadiesHubShadows.card,
      }}
      data-testid={`curated-product-${product.ad_id}`}
    >
      {/* Image */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '1',
          background: '#FEF3C7',
          overflow: 'hidden',
        }}
      >
        {imageUrl && !isSvgPlaceholder ? (
          <WasslaImage
            src={imageUrl}
            alt={title}
            fallbackType="ad"
            testid={`curated-image-${product.ad_id}`}
            showCaption={false}
          />
        ) : null}
        {isSvgPlaceholder ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              background: '#faf5ff',
            }}
          >
            <img src={imageUrl} alt={categoryKey} style={{ width: 48, height: 48, opacity: 0.7 }} />
          </div>
        ) : (
          <div
            style={{
              display: imageUrl && !isSvgPlaceholder ? 'none' : 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#D97706',
              gap: 4,
            }}
          >
            <Package size={24} />
            <span style={{ fontSize: 10 }}>No image</span>
          </div>
        )}

        {/* Verified Badge Overlay */}
        {isVerified && (
          <div
            style={{
              position: 'absolute',
              top: 6,
              left: isRTL ? 'auto' : 6,
              right: isRTL ? 6 : 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              padding: '3px 6px',
              borderRadius: 6,
              background: 'rgba(16,185,129,0.9)',
              color: '#fff',
              fontSize: 9,
              fontWeight: 600,
            }}
            data-testid={`verified-badge-${product.ad_id}`}
          >
            <CheckCircle size={10} />
            {lang === 'ar' ? 'موثق' : 'Verified'}
          </div>
        )}

        {/* Favorite Button */}
        <motion.button
          whileTap={{ scale: 0.85 }}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(product.ad_id);
          }}
          style={{
            position: 'absolute',
            top: 6,
            right: isRTL ? 'auto' : 6,
            left: isRTL ? 6 : 'auto',
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.9)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: isFavorite ? '#DC2626' : colors.muted,
          }}
          data-testid={`curated-fav-${product.ad_id}`}
        >
          <Heart size={14} fill={isFavorite ? 'currentColor' : 'none'} />
        </motion.button>
      </div>

      {/* Info */}
      <div style={{ padding: 10 }}>
        <h4
          style={{
            margin: 0,
            fontSize: 12,
            fontWeight: 600,
            color: colors.ink,
            lineHeight: 1.3,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </h4>
        <p
          style={{
            margin: '4px 0 0',
            fontSize: 13,
            fontWeight: 700,
            color: colors.primary,
          }}
        >
          {formatMoney(product.price, { locale: lang === 'ar' ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
        </p>
        {product.price_dropped && product.previous_price && (
          <div className="cardPriceDrop" style={{ margin: '3px 0 0', fontSize: 10 }} data-testid={`curated-price-drop-${product.ad_id}`}>
            <TrendingDown size={10} />
            <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>
              {formatMoney(product.previous_price, { locale: lang === 'ar' ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
            </span>
            {' '}{lang === 'ar' ? 'السعر انخفض' : 'Price dropped'}
          </div>
        )}
        {location && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              marginTop: 4,
              fontSize: 10,
              color: colors.muted,
            }}
          >
            <MapPin size={10} />
            <span>{location}</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

export default function LadiesHubCuratedRow({
  title,
  products,
  loading,
  onNavigate,
  onToggleFavorite,
  isFavorite = () => false,
  lang = 'en',
  isRTL = false,
}) {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (!scrollRef.current) return;
    const scrollAmount = 180;
    const actualScroll = isRTL ? -direction * scrollAmount : direction * scrollAmount;
    scrollRef.current.scrollBy({ left: actualScroll, behavior: 'smooth' });
  };

  // Don't render if no products and not loading
  if (!loading && (!products || products.length === 0)) {
    return null;
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={motionConfig.fadeSlide}
      style={{
        marginBottom: spacing.lg,
        padding: `0 ${spacing.sm}px`,
      }}
      data-testid={`curated-row-${title?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: spacing.sm,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 700,
            color: colors.ink,
          }}
        >
          {title}
        </h3>

        {/* Scroll Arrows */}
        <div style={{ display: 'flex', gap: 6 }}>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll(-1)}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: colors.chipBg,
              border: `1px solid ${colors.chipBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.primary,
            }}
            aria-label="Previous"
          >
            {isRTL ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => scroll(1)}
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: colors.chipBg,
              border: `1px solid ${colors.chipBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: colors.primary,
            }}
            aria-label="Next"
          >
            {isRTL ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          </motion.button>
        </div>
      </div>

      {/* Scrollable Row */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          gap: spacing.sm,
          overflowX: 'auto',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          padding: '4px 0',
          WebkitOverflowScrolling: 'touch',
        }}
        className="hide-scrollbar"
      >
        {loading ? (
          // Skeleton loading
          Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={`skel-${i}`} />)
        ) : (
          products.map((product) => (
            <CuratedProductCard
              key={product.ad_id}
              product={product}
              onNavigate={onNavigate}
              onToggleFavorite={onToggleFavorite}
              isFavorite={isFavorite(product.ad_id)}
              lang={lang}
              isRTL={isRTL}
            />
          ))
        )}
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        html.dark [data-testid^="curated-row-"] h3 {
          color: #FED7AA !important;
        }
        html.dark [data-testid^="curated-product-"] {
          background: #292524 !important;
          border-color: rgba(255,255,255,0.08) !important;
        }
        html.dark [data-testid^="curated-product-"] h4 {
          color: #FED7AA !important;
        }
      `}</style>
    </motion.section>
  );
}
